import { useInfinityActionGroups, useQueryOptionsSetter } from "hooks";

import { useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { UseInfiniteQueryOptions } from "react-query";
import { useSearchParams } from "react-router-dom";
import { IActionGroupsProps } from "services";
import { FullActionGroupType } from "types";
import { IAxiosResponseWithPagination } from "types/requests";

import { getRange, getSearchParams } from "utils";

type ActionGroupParamsTypes = Partial<IActionGroupsProps["params"]>;
interface IInfiniteQueryActionGroupsProps
  extends Partial<
      UseInfiniteQueryOptions<IAxiosResponseWithPagination<FullActionGroupType>>
    >,
    ActionGroupParamsTypes {}

export function useActionGroupsList(
  params: Partial<
    ReturnType<typeof getSearchParams> & IInfiniteQueryActionGroupsProps
  >,
  mapWidth?: number | undefined,
  disabled = false
) {
  const [isMount, setMount] = useState(false);
  useEffect(() => {
    setMount(true);
  }, []);
  const { ref, inView } = useInView();

  const rawParams = (params ?? {}) as {
    zoom?: number;
    lat?: number | string;
    lng?: number | string;
    withDeleted?: boolean;
    [key: string]: any;
  };

  const { zoom, lat, lng, withDeleted, ...restParams } = rawParams;

  const latProvided = lat !== undefined && lat !== null && lat !== "";
  const lngProvided = lng !== undefined && lng !== null && lng !== "";

  const latNumber = latProvided ? Number(lat) : undefined;
  const lngNumber = lngProvided ? Number(lng) : undefined;

  const hasValidCoordinates =
    latProvided &&
    lngProvided &&
    typeof latNumber === "number" &&
    typeof lngNumber === "number" &&
    Number.isFinite(latNumber) &&
    Number.isFinite(lngNumber);

  const normalizedLat = hasValidCoordinates ? (latNumber as number) : undefined;
  const normalizedLng = hasValidCoordinates ? (lngNumber as number) : undefined;

  const computedRange =
    normalizedLat !== undefined
      ? getRange(zoom, normalizedLat, mapWidth)
      : undefined;

  const sanitizedRange =
    typeof computedRange === "number" && Number.isFinite(computedRange)
      ? computedRange
      : undefined;

  const { data, fetchNextPage, ...rest } = useInfinityActionGroups({
    getNextPageParam: (lastPage) => {
      if (lastPage.data.meta.currentPage < lastPage.data.meta.totalPages) {
        return lastPage.data.meta.currentPage + 1;
      }
    },

    ...restParams,
    range: sanitizedRange,
    lat:
      normalizedLat !== undefined ? normalizedLat.toString() : undefined,
    lng:
      normalizedLng !== undefined ? normalizedLng.toString() : undefined,
    keepPreviousData: true,
    suspense: !isMount,
    refetchOnWindowFocus: false,
    enabled: !disabled,
    withDeleted:
      typeof withDeleted === "boolean" ? withDeleted : !!params?.user,
  });
  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView]);

  return { data, inViewRef: ref, fetchNextPage, ...rest };
}

export function useGroupListFilterParams(mapWidth: number, disabled = false) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, inViewRef } = useActionGroupsList(
    useMemo(() => getSearchParams(searchParams), [searchParams]),
    mapWidth,
    disabled
  );
  const setQueryOptions = useQueryOptionsSetter(searchParams, setSearchParams);

  return { data, inViewRef, setQueryOptions, searchParams };
}
